import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FundingBatchDetailStatus } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    ListFundingBatchSummaryRequest,
    ListResponseFundingBatchSummaryDto
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/supplier/funding-batch-summary/list-funding-batch-summary';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { mapSupplierTransferSortColumn } from '../../helpers/sort-column-mapping';
import {
    mapLegacyStatusTransferType,
    mapSupplierTransferTypeToLegacyStatus
} from '../../helpers/status-mapping';
import { SortDirection, TransfersInput, TransfersResponse } from '../../model/models';

@Injectable({ providedIn: 'root' })
export class ListFundingBatchSummaryService extends ApiService<
    ListFundingBatchSummaryRequest,
    ListResponseFundingBatchSummaryDto
> {
    public requestType = ListFundingBatchSummaryRequest;
    public responseType = ListResponseFundingBatchSummaryDto;
    public endpointName = 'supplier/FundingBatchSummary/ListFundingBatchSummary';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        combineLatest([
            this._featureFlagService.isOn(FeatureFlags.PayLater),
            this._featureFlagService.isOn(FeatureFlags.ListFundingBatchSummaryPerformance)
        ])
            .pipe(
                startWith([
                    this._featureFlagService.isOnSync(FeatureFlags.PayLater),
                    this._featureFlagService.isOnSync(
                        FeatureFlags.ListFundingBatchSummaryPerformance
                    )
                ])
            )
            .subscribe(([payLaterIsOn, performanceTestIsOn]) => {
                this._isPayLaterEnabled = payLaterIsOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else if (performanceTestIsOn) {
                    this.endpointName = 'supplier/FundingBatchSummary/ListFundingBatchSummary';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                } else {
                    this.endpointName = 'ListFundingBatchSummary';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListFundingBatchSummaryRequest
    ): { query: string } | ListFundingBatchSummaryRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const statuses = mapLegacyStatusTransferType(
                req.statuses as FundingBatchDetailStatus[]
            );
            const input: TransfersInput = {
                companyFilter: {
                    supplierIds: [req.refSellerKey]
                },
                transferTypesForSupplier: statuses,
                pagination: {
                    skip: skip || 0,
                    take: take || 0,
                    sortColumn: new EnumType(mapSupplierTransferSortColumn(sortColumn)),
                    sortDirection: new EnumType(
                        SortDirection[sortDirection] || SortDirection.ASCENDING
                    )
                }
            };
            const query = jsonToGraphQLQuery({
                transfers: {
                    __args: { input },
                    rows: {
                        transferDate: true,
                        invoiceCount: true,
                        id: true,
                        status: true,
                        transferTypeForSupplier: true,
                        totalApprovedInvoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        totalDiscountFee: {
                            cents: true,
                            currencyCode: true
                        },
                        totalFundingAndTransactionFees: {
                            cents: true,
                            currencyCode: true
                        },
                        supplierDiscountFee: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerDiscountFee: {
                            cents: true,
                            currencyCode: true
                        },
                        supplierPaymentAmount: {
                            cents: true,
                            currencyCode: true
                        }
                    },
                    totals: {
                        transferCount: true
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<TransfersResponse> | ListResponseFundingBatchSummaryDto
    ): ListResponseFundingBatchSummaryDto {
        if (this._isPayLaterEnabled) {
            const { rows, totals } = (res as GraphQLResponse<TransfersResponse>).data.transfers;
            const mappedResp = new ListResponseFundingBatchSummaryDto({
                totalCount: totals.transferCount,
                rows: rows.map((transfer) => ({
                    buyerDiscountAmount: transfer.buyerDiscountFee,
                    fundingDate: transfer.transferDate,
                    invoiceCount: transfer.invoiceCount,
                    refFundingBatchKey: transfer.id,
                    status: mapSupplierTransferTypeToLegacyStatus(transfer.transferTypeForSupplier),
                    supplierDiscountAmount: transfer.supplierDiscountFee,
                    totalApprovedAmount: transfer.totalApprovedInvoiceAmount,
                    totalDiscountAmount: transfer.totalDiscountFee,
                    totalFundingAmount: transfer.supplierPaymentAmount
                }))
            });
            return mappedResp;
        }
        return res as ListResponseFundingBatchSummaryDto;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
